import { useLocation, useNavigate } from 'react-router-dom'
import clsx from 'clsx'

import { Logo } from '@/common/assets/svg'
import { AccountType } from '@/common/types'
import { ADVISER_NAV_ITEMS } from '@/adviser/adviserNavConfig'
import { isAdviserWorkspaceRoute, isPrimaryAdviserContext } from './accountRoutes'
import useAccountSwitcher from '@/account/useAccountSwitcher'
import useAccountStore from '@/account/accountStore'
import { usePrimaryAccount } from '@/account/usePrimaryAccount'
import { Sheet, SheetContent } from '../ui/sheet'
import { Button } from '../ui'

type Props = {
    open: boolean
    onClose: () => void
}

export function AdviserMobileSlideNav({ open, onClose }: Props) {
    const { pathname } = useLocation()
    const navigate = useNavigate()
    const switchAccount = useAccountSwitcher()
    const primaryAccount = usePrimaryAccount()
    const activeAccountId = useAccountStore((s) => s.activeAccountId)

    const handleNavigation = async (to: string) => {
        const isAdviserInternalRoute = isAdviserWorkspaceRoute(to)

        if (isAdviserInternalRoute && primaryAccount?.accountType === AccountType.ADVISER) {
            if (primaryAccount.accountId !== activeAccountId) {
                // Move to route first; switchAccount owns the full switch-session lifecycle.
                navigate(to)
                await switchAccount(primaryAccount.accountId)
                onClose()
                return
            }
        }
        navigate(to)
        onClose()
    }

    return (
        <Sheet open={open}>
            <SheetContent
                side="right"
                onClose={onClose}
                onInteractOutside={onClose}
                className="w-64 p-0 border-l-0 rounded-tl-2xl rounded-bl-2xl overflow-hidden"
            >
                <div className="flex flex-col h-full bg-white">
                    {/* Header */}
                    <div className="px-2 pt-16 pb-4 border-b border-border-outline flex flex-col gap-8">
                        <div className="px-4">
                            <Logo width={100} height={30} />
                        </div>
                    </div>

                    {/* Nav items */}
                    <div className="flex-1 px-2 py-4 flex flex-col gap-8">
                        <div className="flex flex-col gap-2">
                            {ADVISER_NAV_ITEMS.map((item) => {
                                const Icon = item.icon
                                const isAccountItem = item.key === 'account'
                                const primaryContext = isPrimaryAdviserContext({ primaryAccount, activeAccountId })
                                const isActive = isAccountItem
                                    ? pathname.startsWith(item.to) && primaryContext
                                    : pathname.startsWith(item.to)

                                return (
                                    <Button
                                        variant="ghost"
                                        aria-label={item.ariaLabel}
                                        key={item.key}
                                        onClick={() => void handleNavigation(item.to)}
                                        midClassName="justify-start"
                                        className={clsx('h-12 items-start border-0', {
                                            'bg-brand-accent-soft-lime': isActive,
                                            'hover:bg-lime-50': !isActive,
                                        })}
                                    >
                                        <div className="h-full flex justify-start items-start gap-4">
                                            <Icon size={24} strokeWidth={1.5} className="text-brand-primary-forest" />
                                            <span className="h-full text-brand-primary-forest text-base font-medium leading-6 text-left">
                                                {item.label}
                                            </span>
                                        </div>
                                    </Button>
                                )
                            })}
                        </div>
                    </div>
                </div>
            </SheetContent>
        </Sheet>
    )
}
